import { useMemo } from 'react';
import type { ComponentPropsWithRef, JSX } from 'react';

import styled from '@emotion/styled';
import { useLocation } from 'react-router';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { memo } from '@change-corgi/core/react/core';
import { parseHtml } from '@change-corgi/core/react/html';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { genReplaceTags } from './genReplaceTags';
import type { ReplaceTagsOptions } from './genReplaceTags';

type Props = Omit<ComponentPropsWithRef<typeof Text>, 'variant' | 'children' | 'ellipsis'> &
	ReplaceTagsOptions & {
		html: string;
		as?: React.ElementType;
	};

export const RESTRICTED_TEXT_TAGS = ['strong', 'em', 'b', 'i', 'p', 'ul', 'ol', 'li'];
export const RESTRICTED_TAGS = ['br', 'a', ...RESTRICTED_TEXT_TAGS];
export const RESTRICTED_ATTRS = ['href'];
export const ALLOWED_IFRAME_ATTRS = ['src', 'scrolling', 'frameborder', 'allowfullscreen', 'width', 'height'];

const CustomText = styled(Text)`
	ul,
	ol {
		margin: 1.5em 3em;
	}
	ul {
		list-style-type: circle;
	}
	ol {
		list-style-type: decimal;
	}
	ul > li,
	ol > li {
		margin: 0.75em 0.5em;
	}
	p + p {
		margin-top: 1.5em;
	}
	img,
	video,
	iframe {
		max-width: 100%;
	}
`;

export { genReplaceTags };
export type { ReplaceTagsOptions };

export const Html = memo(
	({
		html,
		onClickLink,
		linkVariant,
		stripLinks,
		supportHashLinks,
		linksTarget,
		transformLinkHref,
		reportBadLinks,
		reportParams,
		as,
		transformers,
		noRestrictedTags,
		noRestrictedAttrs,
		noContentStyles,
		allowIframes,
		className,
		breakWord,
		ref,
		expressive,
		...rest
	}: Props & { ref?: React.RefObject<HTMLDivElement> }): JSX.Element => {
		const location = useLocation();
		const utilityContext = useUtilityContext();

		const { restrictedTags, restrictedAttrs, allowedTags, allowedAttrs } = useMemo(() => {
			const tags = noRestrictedTags ? undefined : RESTRICTED_TAGS;
			const attrs = noRestrictedAttrs ? undefined : RESTRICTED_ATTRS;
			const allowed = allowIframes ? { tags: ['iframe'], attrs: ALLOWED_IFRAME_ATTRS } : undefined;
			if (!transformers) {
				return {
					restrictedAttrs: attrs,
					restrictedTags: tags,
					allowedTags: allowed?.tags,
					allowedAttrs: allowed?.attrs,
				};
			}
			return {
				restrictedTags: !tags
					? undefined
					: ([] as string[]).concat(tags, ...transformers.map((transformer) => transformer.transformedTags || [])),
				restrictedAttrs: !attrs
					? undefined
					: ([] as string[]).concat(attrs, ...transformers.map((transformer) => transformer.transformedAttrs || [])),
				allowedTags: allowed?.tags,
				allowedAttrs: allowed?.attrs,
			};
		}, [transformers, noRestrictedTags, noRestrictedAttrs, allowIframes]);

		const replace = useMemo(
			() =>
				genReplaceTags(
					{
						onClickLink,
						linkVariant,
						expressive,
						stripLinks,
						reportBadLinks,
						supportHashLinks,
						linksTarget,
						transformLinkHref,
						reportParams,
					},
					location,
					utilityContext,
				),
			[
				onClickLink,
				linkVariant,
				expressive,
				stripLinks,
				reportBadLinks,
				supportHashLinks,
				linksTarget,
				transformLinkHref,
				reportParams,
				location,
				utilityContext,
			],
		);

		const Container = noContentStyles ? 'div' : CustomText;

		return (
			<Container
				as={as || 'div'}
				{...rest}
				expressive={expressive}
				ref={ref}
				className={cn(className, {
					'wrap-break-word': breakWord,
				})}
			>
				{parseHtml(html, {
					replace,
					restrictedTags,
					restrictedAttrs,
					allowedTags,
					allowedAttrs,
					transformers,
				})}
			</Container>
		);
	},
);
